import { useTranslation } from 'react-i18next';
import { Button, Descriptions, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import AlertBox from '@/components/alert-box';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  CALCULATION_AMOUNT_ROWS,
  TRAVEL_ABROAD_CURRENCIES,
  TRAVEL_DOMESTIC_CURRENCY,
  countryNameForDisplay,
} from './constants';
import TravelCurrencySettlement from './TravelCurrencySettlement';
import TravelAbJournal from './TravelAbJournal';

const ORDER_TEXT_FIELDS = [
  'travelOrderNo',
  'year',
  'reason',
  'country',
  'city',
  'numberOfDays',
];

/**
 * Fourth wizard step — a read-only recap of the whole travel order, and the
 * one place it can be approved.
 *
 * Approving lives here rather than only in the HR requests list because this is
 * the only view that shows the settlement in full: whoever signs the trip off
 * sees exactly what they are signing.
 */
export default function TravelSummaryStep({
  travelOrder = {},
  travelOrderCalculation = {},
  travelExchangeRates = [],
  ratesDisabled,
  isSavingRates,
  onRatesSubmit,
  requestId,
  canBook = false,
  status,
  canApprove = false,
  isApproving = false,
  onApprove,
  travelSubmission,
  canSubmit = false,
  isSubmitting = false,
  onSubmitForApproval,
}) {
  const { t, i18n } = useTranslation(['officialTravel']);

  const abroadCurrency =
    travelOrder?.dailyWageForAbroadCurrency || TRAVEL_ABROAD_CURRENCIES[0];

  const isApproved = (status?.key || status) === 'ACCEPTED';
  const submittedAt = travelSubmission?.at;

  const formatAmount = (value, isCountry) =>
    `${new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0)} ${
      isCountry ? TRAVEL_DOMESTIC_CURRENCY : abroadCurrency
    }`;

  const formatTimestamp = (value) =>
    value ? dayjs(Number(value)).format(DATE_FORMATS.DISPLAY_WITH_TIME) : '-';

  // Only the lines that carry a payout are worth repeating here; the full
  // breakdown lives on the calculation step and in the document.
  const payoutRows = CALCULATION_AMOUNT_ROWS.filter(({ key }) =>
    [
      'totalDailyWageForCountry',
      'totalDailyWageForAbroad',
      'totalCostsCountry',
      'totalCostsAbroad',
      'totalAmountForPaymentCountry',
      'totalAmountForPaymentAbroad',
    ].includes(key)
  );

  return (
    <div className="grid gap-4">
      {/* Alert boxes stick to the top by default; on this long recap they
          would pile up over the tables while scrolling. */}
      <AlertBox variant="info" className="!static mb-0">
        <p>{t('summary.description', { ns: 'officialTravel' })}</p>
      </AlertBox>

      <AlertBox
        variant={isApproved ? 'success' : 'warning'}
        className="!static mb-0"
      >
        <p>
          {isApproved
            ? t('summary.approved', { ns: 'officialTravel' })
            : t('summary.notApproved', { ns: 'officialTravel' })}
        </p>
      </AlertBox>

      {submittedAt && !isApproved && (
        <AlertBox variant="info" className="!static mb-0">
          <p>
            {t('summary.submitted', {
              ns: 'officialTravel',
              date: dayjs(submittedAt).format(DATE_FORMATS.DISPLAY_WITH_TIME),
              name: travelSubmission?.byName || '-',
            })}
          </p>
        </AlertBox>
      )}

      {/* The employee cannot approve; this is how they say the order is done. */}
      {!isApproved && canSubmit && (
        <div className="flex items-center justify-end gap-3">
          {!submittedAt && (
            <span className="text-sm text-gray-500">
              {t('summary.submitHint', { ns: 'officialTravel' })}
            </span>
          )}
          <Popconfirm
            placement="topRight"
            title={t('summary.submitConfirm', { ns: 'officialTravel' })}
            onConfirm={onSubmitForApproval}
          >
            <Button
              type="primary"
              shape="round"
              className="btn-primary"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {submittedAt
                ? t('actions.resubmit', { ns: 'officialTravel' })
                : t('actions.submit', { ns: 'officialTravel' })}
            </Button>
          </Popconfirm>
        </div>
      )}

      {!isApproved && canApprove && (
        <div className="flex justify-end">
          <Popconfirm
            placement="topRight"
            title={t('summary.approveConfirm', { ns: 'officialTravel' })}
            onConfirm={onApprove}
          >
            <Button
              type="primary"
              shape="round"
              className="btn-primary"
              disabled={isApproving}
              loading={isApproving}
            >
              {t('actions.approve', { ns: 'officialTravel' })}
            </Button>
          </Popconfirm>
        </div>
      )}

      <Descriptions
        bordered
        size="small"
        column={1}
        title={t('order.title', { ns: 'officialTravel' })}
      >
        {ORDER_TEXT_FIELDS.map((field) => (
          <Descriptions.Item
            key={field}
            label={t(`order.${field}`, { ns: 'officialTravel' })}
          >
            {(field === 'country'
              ? countryNameForDisplay(
                  travelOrder?.countryCode,
                  i18n.language,
                  travelOrder?.country
                )
              : travelOrder?.[field]) || '-'}
          </Descriptions.Item>
        ))}

        <Descriptions.Item
          label={t('order.startDate', { ns: 'officialTravel' })}
        >
          {travelOrder?.startDate
            ? dayjs(travelOrder.startDate).format(DATE_FORMATS.DISPLAY)
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('order.transportType', { ns: 'officialTravel' })}
        >
          {(travelOrder?.transportType || [])
            .map((value) =>
              t(`options.transport.${value}`, { ns: 'officialTravel' })
            )
            .join(', ') || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('order.paidAccommodation', { ns: 'officialTravel' })}
        >
          {travelOrder?.paidAccommodation
            ? t('options.yes', { ns: 'officialTravel' })
            : t('options.no', { ns: 'officialTravel' })}
        </Descriptions.Item>

        <Descriptions.Item label={t('order.paidFood', { ns: 'officialTravel' })}>
          {travelOrder?.paidFood
            ? t(`options.food.${travelOrder.paidFood}`, {
                ns: 'officialTravel',
              })
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('order.dailyWageForCountry', { ns: 'officialTravel' })}
        >
          {formatAmount(travelOrder?.dailyWageForCountry, true)}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('order.dailyWageForAbroad', { ns: 'officialTravel' })}
        >
          {formatAmount(travelOrder?.dailyWageForAbroad, false)}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        bordered
        size="small"
        column={1}
        title={t('calculation.title', { ns: 'officialTravel' })}
      >
        <Descriptions.Item
          label={t('calculation.startDateAndTime', { ns: 'officialTravel' })}
        >
          {formatTimestamp(travelOrderCalculation?.startDateAndTime)}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('calculation.endDateAndTime', { ns: 'officialTravel' })}
        >
          {formatTimestamp(travelOrderCalculation?.endDateAndTime)}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('calculation.totalNumberOfHours', { ns: 'officialTravel' })}
        >
          {travelOrderCalculation?.totalNumberOfHours ?? 0}
        </Descriptions.Item>

        {payoutRows.map(({ key, country }) => (
          <Descriptions.Item
            key={key}
            label={t(`calculation.${key}`, { ns: 'officialTravel' })}
          >
            {formatAmount(travelOrderCalculation?.[key], country)}
          </Descriptions.Item>
        ))}

        <Descriptions.Item
          label={t('calculation.attachmentNumber', { ns: 'officialTravel' })}
        >
          {travelOrderCalculation?.attachmentNumber ?? 0}
        </Descriptions.Item>
      </Descriptions>

      <TravelCurrencySettlement
        travelExchangeRates={travelExchangeRates}
        travelOrderCalculation={travelOrderCalculation}
        disabled={ratesDisabled}
        isPending={isSavingRates}
        onSubmit={onRatesSubmit}
      />

      {/* The journal books what was approved, so it waits for the approval. */}
      {isApproved && canBook && requestId && (
        <TravelAbJournal requestId={requestId} />
      )}
    </div>
  );
}
